import React, { useEffect, useState } from 'react';
import { MessageSquare, CheckCheck, Eye, XCircle, Inbox, Send, Megaphone } from 'lucide-react';
import { whatsappService } from 'api/services/whatsapp.service';
import { localDayKey } from 'pages/dashboardScope';

// Real WhatsApp analytics — computed live from messages + campaigns (no mock data).
const WhatsAppDashboard: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const today = new Date();
  const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
  // The viewer's (IST) calendar days, sent with the viewer's zone so the server counts
  // each message into the same day (2026-10-14: these were UTC days, 05:30 off).
  const [from, setFrom] = useState(localDayKey(monthAgo));
  const [to, setTo] = useState(localDayKey(today));

  const load = async () => {
    setLoading(true);
    setError(null);
    const res = await whatsappService.getAnalytics({
      startDate: new Date(from + 'T00:00:00').getTime(),
      endDate: new Date(to + 'T23:59:59.999').getTime(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata',
    });
    if (res.success && res.data) setData(res.data);
    else setError(res.error || 'Failed to load analytics');
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const t = data?.totals || {};
  const r = data?.rates || {};
  const series: any[] = data?.dailySeries || [];

  // The API only returns days that actually have messages, so a 30-day range
  // can come back as 15 entries. Plotting those side by side silently closes
  // the gaps and misstates *when* things happened — a quiet week would look
  // like a busy one. Fill the missing days with zeros so one bar is one day.
  const days = React.useMemo(() => {
    const byDate = new Map(series.map((d) => [d.date, d]));
    const out: any[] = [];
    const cursor = new Date(from + 'T00:00:00Z');
    const end = new Date(to + 'T00:00:00Z');
    while (cursor <= end) {
      const key = cursor.toISOString().slice(0, 10);
      out.push(byDate.get(key) || { date: key, reached: 0, failed: 0, delivered: 0, read: 0, sent: 0 });
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return out;
  }, [series, from, to]);

  const maxDay = Math.max(1, ...days.map((d) => (d.reached || 0) + (d.failed || 0)));
  // Label roughly 8 ticks whatever the range, so they never collide.
  const labelEvery = Math.max(1, Math.ceil(days.length / 8));
  const fmtDay = (iso: string) =>
    new Date(iso + 'T00:00:00Z').toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });
  const byType: Record<string, number> = data?.byType || {};
  const failures: any[] = data?.failureBreakdown || [];
  const campaigns = data?.campaigns || {};

  const Card = ({ icon, label, value, color }: any) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">{label}</p>
        <span className={color}>{icon}</span>
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{(value ?? 0).toLocaleString()}</p>
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">WhatsApp Analytics</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Live delivery, engagement and campaign metrics</p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-800 dark:text-white" />
          <span className="text-gray-400">to</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
            className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-800 dark:text-white" />
          <button onClick={load} className="tl-btn tl-btn-primary">Apply</button>
        </div>
      </div>

      {loading && <p className="text-gray-500 dark:text-gray-400">Loading analytics…</p>}
      {error && <p className="text-red-600 dark:text-red-400">{error}</p>}

      {!loading && !error && data && (
        <>
          {/* Headline cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <Card icon={<Send className="w-4 h-4" />} label="Outbound" value={t.outbound} color="text-blue-500" />
            <Card icon={<CheckCheck className="w-4 h-4" />} label="Reached" value={(t.delivered || 0) + (t.read || 0)} color="text-green-500" />
            <Card icon={<Eye className="w-4 h-4" />} label="Read" value={t.read} color="text-purple-500" />
            <Card icon={<XCircle className="w-4 h-4" />} label="Failed" value={t.failed} color="text-red-500" />
            <Card icon={<Inbox className="w-4 h-4" />} label="Inbound" value={t.inbound} color="text-amber-500" />
            <Card icon={<MessageSquare className="w-4 h-4" />} label="Pending" value={t.pending} color="text-gray-400" />
          </div>

          {/* Rates */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
              <p className="text-xs text-green-700 dark:text-green-400 font-medium">Delivery rate</p>
              <p className="text-2xl font-bold text-green-700 dark:text-green-300">{r.deliveryRatePct ?? 0}%</p>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
              <p className="text-xs text-purple-700 dark:text-purple-400 font-medium">Read rate</p>
              <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">{r.readRatePct ?? 0}%</p>
            </div>
            <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
              <p className="text-xs text-red-700 dark:text-red-400 font-medium">Failure rate</p>
              <p className="text-2xl font-bold text-red-700 dark:text-red-300">{r.failureRatePct ?? 0}%</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Daily series */}
            <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Daily outbound (reached vs failed)</h3>
              {series.length === 0 ? (
                <p className="text-sm text-gray-400">No data in this range.</p>
              ) : (
                <div className="flex gap-2">
                  {/* y scale */}
                  <div className="flex flex-col justify-between h-40 shrink-0 text-right text-[10px] tabular-nums"
                       style={{ color: 'var(--text-muted)' }}>
                    <span>{maxDay.toLocaleString()}</span>
                    <span>{Math.round(maxDay / 2).toLocaleString()}</span>
                    <span>0</span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="relative h-40" style={{ borderLeft: '1px solid var(--divider)', borderBottom: '1px solid var(--divider)' }}>
                      {/* midpoint gridline, recessive */}
                      <div className="absolute inset-x-0 top-1/2" style={{ borderTop: '1px dashed var(--divider)' }} />
                      <div className="absolute inset-0 flex items-end gap-px px-px">
                        {days.map((d) => {
                          const reached = d.reached || 0; const failed = d.failed || 0;
                          const total = reached + failed;
                          const pct = total ? Math.round((failed / total) * 100) : 0;
                          return (
                            // h-full is load-bearing: the row is `items-end`, which sizes
                            // each column to its content. Without a definite height the
                            // bars' percentage heights resolve against zero and the whole
                            // chart renders blank.
                            <div
                              key={d.date}
                              className="flex-1 h-full flex flex-col justify-end group"
                              title={`${fmtDay(d.date)} — ${total.toLocaleString()} sent · ${reached.toLocaleString()} reached · ${failed.toLocaleString()} failed (${pct}% failed)`}
                            >
                              <div className="w-full rounded-t transition-opacity group-hover:opacity-80"
                                   style={{ height: `${(failed / maxDay) * 100}%`, background: 'var(--danger-text)' }} />
                              <div className="w-full transition-opacity group-hover:opacity-80"
                                   style={{ height: `${(reached / maxDay) * 100}%`, background: 'var(--toggle-on)' }} />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    {/* date axis — every Nth tick so labels never collide */}
                    <div className="flex gap-px px-px mt-1.5">
                      {days.map((d, i) => (
                        <div key={d.date} className="flex-1 text-center text-[10px] whitespace-nowrap"
                             style={{ color: 'var(--text-muted)' }}>
                          {i % labelEvery === 0 || i === days.length - 1 ? fmtDay(d.date) : ''}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              <div className="flex gap-4 mt-2 text-xs text-gray-500">
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-green-500 inline-block rounded" /> Reached</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-400 inline-block rounded" /> Failed</span>
              </div>
            </div>

            {/* Campaigns + by type */}
            <div className="space-y-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2"><Megaphone className="w-4 h-4" /> Campaigns</h3>
                <div className="flex justify-between text-sm py-1"><span className="text-gray-500">Total</span><span className="font-semibold dark:text-white">{campaigns.total ?? 0}</span></div>
                <div className="flex justify-between text-sm py-1"><span className="text-gray-500">Running</span><span className="font-semibold text-blue-600">{campaigns.running ?? 0}</span></div>
                <div className="flex justify-between text-sm py-1"><span className="text-gray-500">Completed</span><span className="font-semibold text-green-600">{campaigns.completed ?? 0}</span></div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Outbound by type</h3>
                {Object.keys(byType).length === 0 ? <p className="text-sm text-gray-400">No data.</p> :
                  Object.entries(byType).map(([type, n]) => (
                    <div key={type} className="flex justify-between text-sm py-1"><span className="text-gray-500">{type}</span><span className="font-semibold dark:text-white">{n.toLocaleString()}</span></div>
                  ))}
              </div>
            </div>
          </div>

          {/* Failure breakdown */}
          {failures.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Failure reasons</h3>
              <div className="space-y-1">
                {failures.map((f) => (
                  <div key={f.code} className="flex items-center justify-between text-sm bg-red-50 dark:bg-red-900/20 rounded px-3 py-1.5">
                    <span className="text-gray-700 dark:text-gray-300"><span className="font-mono text-xs text-red-600 dark:text-red-400 mr-2">{f.code}</span>{f.reason}</span>
                    <span className="font-semibold text-red-700 dark:text-red-300">{(f.count ?? 0).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WhatsAppDashboard;
